import {
  Anchor,
  Button,
  Card,
  Code,
  Divider,
  Grid,
  Group,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import useFetch from '../../../helpers/useFetch';
import {
  MARKETING_SUBSCRIPTION,
  defaultSubscriptionLabel,
  effectiveSubscriptions,
  writeCuratedSubscriptions,
} from '../../../helpers/userVisibleSubscriptions';
import classes from './SettingsTabSubscriptionManagement.module.css';

const SettingsTabSubscriptionManagement = ({
  domain,
  currentDomain,
  handleChange,
  testResult,
  testUrl,
  setScheduleSyncSuccess,
  setScheduleSyncError,
}) => {
  const fetch = useFetch();
  const [availableSubscriptions, setAvailableSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [newSubscription, setNewSubscription] = useState('');
  const [newSubscriptionError, setNewSubscriptionError] = useState(null);

  useEffect(() => {
    const loadSubscriptions = async () => {
      try {
        setLoadingSubscriptions(true);
        const response = await fetch(
          `/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}/subscriptions`,
        );
        setAvailableSubscriptions(response.subscriptions || []);
      } catch (error) {
        console.error('Failed to load subscriptions:', error);
        setAvailableSubscriptions([]);
      } finally {
        setLoadingSubscriptions(false);
      }
    };

    if (currentDomain?.accountId && currentDomain?.id) {
      loadSubscriptions();
    }
  }, [currentDomain?.accountId, currentDomain?.id, fetch]);

  let subscriberSyncApiResponse;
  if (testResult?.testSubscriberSync?.response) {
    subscriberSyncApiResponse = (
      <>
        <span>API Response: </span>
        <code>{JSON.stringify(testResult?.testSubscriberSync?.response)}</code>
      </>
    );
  }

  const syncSubscribersNow = () => {
    setScheduleSyncSuccess(undefined);
    setScheduleSyncError(undefined);
    fetch(`/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}/scheduleSubscriberSync`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
      .then((result) => {
        setScheduleSyncSuccess(true);
      })
      .catch((e) => setScheduleSyncError(e.message));
  };

  // What the subscription manager renders today, which is not always what the column
  // holds: an uncurated domain (`[]`, the default) exposes Marketing through an API
  // fallback. Editing therefore starts from this list, not from the raw column, so the
  // first tick materialises what was implicit instead of dropping it.
  const visibleSubscriptions = effectiveSubscriptions(domain.userVisibleSubscriptions);

  // Keyed lowercase for the same reason subscriptionRows is collapsed below: a curated
  // entry can be stored under more than one spelling. Unlike readCuratedSubscriptions'
  // exact-key dedup, take the first NON-EMPTY label rather than the first entry's label,
  // so a stored label is not blanked by an empty legacy-spelling duplicate that happens
  // to sort first.
  const labelsByKey = new Map();
  for (const { key, label } of visibleSubscriptions) {
    const normalized = key.toLowerCase();
    if (!labelsByKey.get(normalized)) {
      labelsByKey.set(normalized, label);
    }
  }

  // Subscription keys are compared case-INSENSITIVELY throughout this tab. The same
  // subscription reaches it under more than one spelling: the subscription manager used to
  // post the legacy map keyed by DEFAULT DISPLAY NAME, so the registry carries `Marketing`
  // where curation writes `marketing`, and magellan-api registers whatever a profile write
  // supplied. Comparing exactly rendered both as separate rows - one ticked, one not, for a
  // single subscription.
  const sameSubscription = (a, b) => a.toLowerCase() === b.toLowerCase();

  const commitSubscriptions = (subscriptions) => {
    handleChange({
      target: {
        name: 'userVisibleSubscriptions',
        value: writeCuratedSubscriptions(subscriptions),
      },
    });
  };

  const handleUserVisibleSubscriptionsChange = (subscriptionName, isVisible) => {
    if (isVisible) {
      commitSubscriptions([...visibleSubscriptions, { key: subscriptionName, label: '' }]);
      return;
    }

    // By alias, not by exact string: a row is labelled from the registry's spelling while the
    // curated entry may hold another (`Marketing` vs `marketing`), and an exact comparison
    // would leave the entry behind - the switch would spring back on the next render.
    commitSubscriptions(visibleSubscriptions.filter(({ key }) => !sameSubscription(key, subscriptionName)));
  };

  const handleSubscriptionLabelChange = (subscriptionName, label) => {
    commitSubscriptions(
      visibleSubscriptions.map((subscription) =>
        sameSubscription(subscription.key, subscriptionName) ? { ...subscription, label } : subscription
      )
    );
  };

  // Every subscription that has a row: the registry the domain has written, plus anything
  // curated that is not in it (a key can be curated before any profile carries it).
  // Marketing leads, matching where the manager renders it. The combined list is then
  // collapsed by lowercased key, keeping the first spelling seen - the registry can hold
  // two spellings of ANY subscription by the same legacy-display-name mechanism described
  // above, not just Marketing, so filtering only the Marketing alias out of the other two
  // sources left every other subscription free to render twice.
  const seenSubscriptionKeys = new Set();
  const allSubscriptionKeys = [
    MARKETING_SUBSCRIPTION,
    ...availableSubscriptions,
    ...visibleSubscriptions.map(({ key }) => key),
  ];
  const subscriptionRows = allSubscriptionKeys.filter((key) => {
    const normalized = key.toLowerCase();
    if (seenSubscriptionKeys.has(normalized)) {
      return false;
    }
    seenSubscriptionKeys.add(normalized);
    return true;
  });

  // Adding a subscription is just curating a key that does not exist yet: it goes into
  // the same list, so the save registers it (magellan-api writes every curated key to
  // the `subscriptions` registry) and it becomes selectable in segment rules, catalog
  // triggers and workflows.
  //
  // The value typed here is the KEY — the string stored on profiles, and the one an
  // integration has to send if it sends subscriptions at all — so it is taken verbatim
  // apart from trimming. Deriving it from a display name would quietly rename what an API
  // client is already sending, and the existing keys in the wild are not all one
  // convention (`promo`, `weeklyNewsletter`).
  const handleAddSubscription = () => {
    const key = newSubscription.trim();

    if (!key) {
      setNewSubscriptionError('Enter a subscription key.');
      return;
    }

    // Case-insensitive, because `Promo` and `promo` would be two subscriptions that
    // read as one — the confusion is worth refusing rather than resolving.
    const clash = subscriptionRows.find((row) => row.toLowerCase() === key.toLowerCase());
    if (clash) {
      setNewSubscriptionError(`${defaultSubscriptionLabel(clash)} already exists — select it in the list instead.`);
      return;
    }

    setNewSubscriptionError(null);
    setNewSubscription('');
    commitSubscriptions([...visibleSubscriptions, { key, label: '' }]);
  };

  return (
    <Card withBorder>
      <Card.Section p="md">
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Subscription Management</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={domain.subscriptionManagement ?? ''}
              onChange={(subscriptionManagement) =>
                handleChange({
                  target: {
                    name: 'subscriptionManagement',
                    value: subscriptionManagement,
                  },
                })
              }
              data={[
                { label: 'External', value: 'external' },
                { label: 'Internal', value: 'internal' },
              ]}
            />
            <Text size="xs" c="dimmed" mt="xs">
              If you keep information in your shop about whether a user is subscribed or not, select{' '}
              <strong>External</strong>. Otherwise, select <strong>Internal</strong>.<br />
              In <strong>Internal</strong> mode, you should only send email and SMS communication through Releva because
              unsubscribes would not propagate to other third-party systems!
            </Text>
          </Grid.Col>
        </Grid>
        {domain.subscriptionManagement === 'internal' ? (
          <>
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                <label>Subscription Request Behavaior</label>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                <SegmentedControl
                  value={domain.subscriptionRequestBehavior ?? ''}
                  onChange={(value) =>
                    handleChange({
                      target: { name: 'subscriptionRequestBehavior', value },
                    })
                  }
                  data={[
                    { label: 'Wait for confirmation', value: 'emitEvent' },
                    { label: 'Subscribe right away', value: 'subscribe' },
                  ]}
                />
                <Text size="xs" c="dimmed" mt="xs">
                  Subscription requests offer you a way to subscribe users from the frontend either directly, or through
                  email confirmation. Direct subscribes are easier for the user but are less secure.
                </Text>
              </Grid.Col>
            </Grid>

            <Divider my="lg" />

            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                <label>User Visible Subscriptions</label>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                <div>
                  {loadingSubscriptions ? (
                    <Text c="dimmed">Loading subscriptions...</Text>
                  ) : (
                    <Stack gap="xs">
                      {/* Headings rather than a label on every row: the display-name field is
                          repeated per selected subscription, so naming the column once says what
                          it is without stuttering down the list. */}
                      <Group gap="sm" wrap="nowrap">
                        <Text size="xs" fw={500} c="dimmed" className={classes.keyColumn}>
                          Visible to users
                        </Text>
                        <Text size="xs" fw={500} c="dimmed" className={classes.nameColumn}>
                          Name shown to users (optional)
                        </Text>
                      </Group>
                      {subscriptionRows.map((subscription) => {
                        const isChecked = labelsByKey.has(subscription.toLowerCase());

                        return (
                          <Group key={subscription} gap="sm" wrap="nowrap" align="center">
                            <Switch
                              id={`userVisibleSubscriptions-${subscription}`}
                              label={defaultSubscriptionLabel(subscription)}
                              checked={isChecked}
                              onChange={(e) => handleUserVisibleSubscriptionsChange(subscription, e.target.checked)}
                              className={classes.keyColumn}
                            />
                            {isChecked && (
                              <TextInput
                                aria-label={`Name shown to users for ${defaultSubscriptionLabel(subscription)}`}
                                placeholder={defaultSubscriptionLabel(subscription)}
                                value={labelsByKey.get(subscription.toLowerCase()) || ''}
                                onChange={(e) => handleSubscriptionLabelChange(subscription, e.target.value)}
                                className={classes.nameColumn}
                              />
                            )}
                          </Group>
                        );
                      })}
                    </Stack>
                  )}
                </div>
                <Group gap="sm" wrap="nowrap" align="flex-start" mt="md">
                  <TextInput
                    aria-label="New subscription key"
                    placeholder="e.g. weeklyNewsletter"
                    description="If your integration sends subscriptions, match its key exactly; if not, it is yours to choose"
                    // Mantine renders the description ABOVE the input by default, pushing the input
                    // below the top of the Group while the button stayed at it. With the input
                    // first, `align="flex-start"` lines the two up, error line or not.
                    inputWrapperOrder={['label', 'input', 'description', 'error']}
                    value={newSubscription}
                    error={newSubscriptionError}
                    disabled={loadingSubscriptions}
                    onChange={(e) => {
                      setNewSubscription(e.target.value);
                      setNewSubscriptionError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        // The tab sits inside the settings form; Enter here means "add
                        // this subscription", not "submit the domain".
                        e.preventDefault();
                        handleAddSubscription();
                      }
                    }}
                    className={classes.keyColumn}
                  />
                  <Button
                    type="button"
                    variant="default"
                    disabled={loadingSubscriptions}
                    onClick={handleAddSubscription}
                  >
                    Add subscription
                  </Button>
                </Group>
                <Text size="xs" c="dimmed" mt="xs">
                  <strong>Note:</strong> Users only see the subscriptions you switch on here. The name beside each one
                  is what they read in the subscription manager — leave it empty to use the name shown next to the
                  switch.
                  <br />
                  <strong>Marketing</strong> is the domain-wide opt-in, so it is listed first wherever it appears. You
                  can hide it while at least one other subscription is switched on; with nothing switched on at all, it
                  is shown on its own.
                  <br />
                  Adding a subscription creates it for this domain. The key is the value stored on profiles: if your
                  integration sends subscriptions, type the key exactly as it sends it, and if it does not, the key is
                  only used here and this list simply decides what users see and can unsubscribe from. Once saved it
                  also becomes available in segment rules, catalog triggers and workflows.
                </Text>
              </Grid.Col>
            </Grid>
          </>
        ) : null}
        {domain.subscriptionManagement === 'external' ? (
          <>
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                <label>Email Unsubscribe URL</label>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                <TextInput
                  name="unsubscribeLink"
                  placeholder="http://myawesomeshop.com/unsubscribe"
                  value={domain.unsubscribeLink || ''}
                  onChange={handleChange}
                />
                <Text size="xs" c="dimmed" mt="xs">
                  You need to implement this url in your shop. We will send users to this link with
                  ?encodedEmail=base64(email)&url=(shopUrl).
                  <br />
                  When a user visits this link, you need to mark the user as unsubscribed in your database, and also
                  send an unsubscribe API call to Releva as described{' '}
                  <Anchor
                    href="https://releva.ai/documentation/gdpr-identity-apis/unsubscribe/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {' '}
                    <IconExternalLink size={14} style={{ verticalAlign: 'middle' }} /> in the docs
                  </Anchor>
                  . <br />
                  <strong>Note:</strong> Leave this empty to rely on per-profile unsubscribe links instead: each profile
                  then needs its own <Code>unsubscribeLink</Code>, set through the profile upsert (
                  <Code>POST /api/v0/profiles</Code>), and that link is what we advertise in the List-Unsubscribe header
                  and the email footer for that recipient.
                  <br />
                  Profiles with no link of their own are skipped for Opt-in Marketing and Opt-out Marketing campaigns,
                  because the email would otherwise carry an unsubscribe link that cannot identify the recipient —
                  transactional campaigns still reach them. Domains connected to Klaviyo are unaffected, since Klaviyo
                  injects its own unsubscribe link into the delivered email.
                  <br />
                  Fill the URL in if you want a single shop-wide page to serve as the fallback for profiles that have no
                  link of their own.
                </Text>
              </Grid.Col>
            </Grid>
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                <label>Subscriber Sync API</label>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                <Group gap="xs">
                  <TextInput
                    name="subscriberSyncApiUrl"
                    placeholder="https://myawesomeshop.com"
                    value={domain.subscriberSyncApiUrl || ''}
                    error={testResult?.testSubscriberSync?.error ? true : false}
                    onChange={handleChange}
                    style={{ flex: 1 }}
                  />
                  <Button
                    disabled={!domain.productSyncApiUrl}
                    variant="default"
                    onClick={() => testUrl('testSubscriberSync', domain.subscriberSyncApiUrl)}
                  >
                    Test
                  </Button>
                  <Button disabled={!domain.subscriberSyncApiUrl} onClick={() => syncSubscribersNow()}>
                    Sync Now
                  </Button>
                </Group>
                <Text size="xs" c="dimmed" mt="xs">
                  Implementing this API allow us to sync your subscribed users to Releva.
                </Text>
                {subscriberSyncApiResponse}
              </Grid.Col>
            </Grid>
          </>
        ) : (
          ''
        )}
      </Card.Section>
    </Card>
  );
};

export default SettingsTabSubscriptionManagement;
